/**
 * Same-origin calls from the login page to the Host's `/sinocst/auth/*` routes.
 * Every response is validated here because it crosses the HTTP boundary.
 */

/** Enterprise SSO entry advertised by `GET /sinocst/auth/sso/options`. */
export interface SsoOptions {
  readonly enabled: boolean
  readonly label: string
  readonly startUrl: string
}

/** Result of a sign-in request. */
export type AuthOutcome =
  /** The Host set the session cookie; navigate to the echoed, validated redirect. */
  | { readonly kind: 'signed-in'; readonly redirect: string }
  /** The backend refused the credentials; `message` is its text when it sent one. */
  | { readonly kind: 'rejected'; readonly message: string | undefined }
  /** The request failed or the Host answered 503 because the backend was unreachable. */
  | { readonly kind: 'unavailable' }

/** Body of `POST /sinocst/auth/login`. */
export interface PasswordLoginRequest {
  readonly username: string
  readonly password: string
  readonly redirect: string
}

/** Body of `POST /sinocst/auth/erp-session`, built from the SSO return query. */
export interface ErpSessionRequest {
  readonly erp_token: string
  readonly user_id?: string
  readonly erp_tenant_id?: string
  readonly mandt?: string
  readonly redirect: string
}

const DEFAULT_REDIRECT = '/'
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/

/**
 * Restrict a post-login target to a same-origin path.
 * @param raw - candidate from the `redirect` query parameter or a Host response.
 * @returns `raw` when it starts with one `/` and holds no control characters, otherwise `/`. Browsers read `/\` as `//` and
 *   strip tabs and newlines, so either could turn the path into another origin.
 */
export function safeRedirect(raw: string | null): string {
  if (raw === null || !/^\/(?![/\\])/.test(raw) || CONTROL_CHARACTER.test(raw)) return DEFAULT_REDIRECT
  return raw
}


function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    // SyntaxError: a non-JSON body (e.g. a proxy error page) carries no usable fields.
    return undefined
  }
}

async function postAuth(path: string, body: PasswordLoginRequest | ErpSessionRequest): Promise<AuthOutcome> {
  let response: Response
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    })
  } catch {
    // TypeError: the network request itself failed, so the Host is unreachable.
    return { kind: 'unavailable' }
  }
  if (response.status === 503) return { kind: 'unavailable' }
  const payload = await readJson(response)
  if (response.ok && isRecord(payload) && payload.success === true && typeof payload.redirect === 'string') {
    return { kind: 'signed-in', redirect: safeRedirect(payload.redirect) }
  }
  const message = isRecord(payload) && typeof payload.message === 'string' && payload.message !== '' ? payload.message : undefined
  return { kind: 'rejected', message }
}

/**
 * Sign in with an account and password.
 * @param request - trimmed username, password, and the validated redirect to echo.
 * @returns the sign-in outcome.
 */
export function loginWithPassword(request: PasswordLoginRequest): Promise<AuthOutcome> {
  return postAuth('/sinocst/auth/login', request)
}

/**
 * Exchange an ERP token from the SSO return for a Host session.
 * @param request - token fields read from the return query and the validated redirect to echo.
 * @returns the sign-in outcome.
 */
export function exchangeErpSession(request: ErpSessionRequest): Promise<AuthOutcome> {
  return postAuth('/sinocst/auth/erp-session', request)
}

/**
 * Read the enterprise SSO entry.
 * @returns the advertised options; a failed request or malformed body yields a disabled entry,
 *   because SSO is an optional alternative to password sign-in.
 */
export async function fetchSsoOptions(): Promise<SsoOptions> {
  const disabled: SsoOptions = { enabled: false, label: '', startUrl: '' }
  let response: Response
  try {
    response = await fetch('/sinocst/auth/sso/options', { credentials: 'same-origin' })
  } catch {
    // TypeError: network failure; password sign-in stays available.
    return disabled
  }
  if (!response.ok) return disabled
  const payload = await readJson(response)
  if (!isRecord(payload) || payload.enabled !== true || typeof payload.start_url !== 'string' || payload.start_url === '') return disabled
  return { enabled: true, label: typeof payload.label === 'string' ? payload.label : '', startUrl: payload.start_url }
}

/** Query keys of an SSO return that carry identity and must not stay in the address bar. */
const SSO_RETURN_KEYS = ['erp_token', 'access_token', 'user_id', 'username', 'erp_tenant_id', 'mandt'] as const

/** Identity fields of an SSO return query. */
export interface SsoReturn {
  readonly erpToken: string
  readonly userId: string
  readonly erpTenantId: string
  readonly mandt: string
}

/**
 * Read the SSO return fields from a query string.
 * @param search - `location.search`.
 * @returns the fields when `erp_token` or `access_token` is present, otherwise `undefined`.
 */
export function readSsoReturn(search: string): SsoReturn | undefined {
  const params = new URLSearchParams(search)
  const field = (...keys: string[]): string => keys.map(key => params.get(key)?.trim() ?? '').find(value => value !== '') ?? ''
  const erpToken = field('erp_token', 'access_token')
  if (erpToken === '') return undefined
  return { erpToken, userId: field('user_id', 'username'), erpTenantId: field('erp_tenant_id'), mandt: field('mandt') }
}

/** Remove the SSO return identity keys from the address bar and history entry without navigating. */
export function stripSsoReturn(): void {
  const url = new URL(window.location.href)
  for (const key of SSO_RETURN_KEYS) url.searchParams.delete(key)
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

/**
 * Build the identity-provider URL for an SSO start.
 * @param startUrl - `start_url` from the SSO options, absolute or same-origin relative.
 * @param origin - the page origin; the return address is `<origin>/login`.
 * @param redirect - validated post-login path carried through the return address.
 * @returns the start URL with `redirect_uri` and `return_url` added unless the backend already set them.
 */
export function ssoStartUrl(startUrl: string, origin: string, redirect: string): string {
  const target = new URL(startUrl, origin)
  const back = new URL('/login', origin)
  if (redirect !== DEFAULT_REDIRECT) back.searchParams.set('redirect', redirect)
  if (!target.searchParams.has('redirect_uri')) target.searchParams.set('redirect_uri', back.href)
  if (!target.searchParams.has('return_url')) target.searchParams.set('return_url', back.href)
  return target.href
}
